import { getFreshIdToken, forceRefreshIdToken } from './firebase';
import { getSelectedTenant, ACTIVE_TENANT_HEADER } from './tenantSession';
import { tenantHeaders } from '../lib/tenantSession';

// Shared authenticated fetch for PROTECTED Business API calls. Restores the established Web session contract:
// the Firebase session (not a login-time localStorage cache) is the primary identity. It reuses the canonical
// token helpers — getFreshIdToken() (awaits initial auth restoration, silently refreshes a token within ~5 min
// of expiry) and the single-flight forceRefreshIdToken() — so it never sends a known-stale token and never
// duplicates refresh logic.
//
// Behaviour (GOV13 §2/§8):
//  - waits for auth restoration and reads a CURRENT id token (never localStorage.crm_access_token);
//  - a null token (signed out / restoration produced no user) yields a synthetic 401 {code:'token_missing'} so
//    callers render session-ended, NOT false-empty data;
//  - on a backend token-expiry/invalid 401, forces ONE shared refresh and retries — but only for idempotent
//    methods (GET/HEAD). POST/purchase already sends a fresh pre-submission token and is never auto-retried,
//    so a Checkout/purchase can never be duplicated by an auth retry;
//  - a 403 is an authorization result, not an auth failure — it is returned as-is and never retried.
export async function authedFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const retryable = method === 'GET' || method === 'HEAD';

  const token = await getFreshIdToken();
  if (!token) {
    return new Response(JSON.stringify({ code: 'token_missing' }),
      { status: 401, headers: { 'Content-Type': 'application/json' } });
  }
  // PMT1 active-tenant context. A multi-membership user's identity does not identify the business on
  // screen, so every protected call states which tenant it is acting in. This is a SELECTION, not an
  // authorisation: the server re-verifies membership in this tenant and fails closed on a miss. An
  // explicit header already set by a caller wins, and a signed-out/unselected state simply omits it.
  const activeTenant = getSelectedTenant();
  const withAuth = (t) => ({
    ...options,
    headers: {
      ...(activeTenant ? { [ACTIVE_TENANT_HEADER]: activeTenant } : {}),
      ...(options.headers || {}),
      Authorization: `Bearer ${t}`, ...tenantHeaders(),
    },
  });

  let res = await fetch(url, withAuth(token));
  if (res.status === 401 && retryable) {
    let code = '';
    try { code = (await res.clone().json())?.code || ''; } catch { /* body not JSON */ }
    if (code === 'token_expired' || code === 'token_invalid' || code === 'token_missing' || code === '') {
      const fresh = await forceRefreshIdToken();   // single-flight — N concurrent 401s → one getIdToken(true)
      if (fresh) res = await fetch(url, withAuth(fresh));
    }
  }
  return res;
}

export default authedFetch;
